import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaCheck, FaMoon, FaPlane, FaShip, FaBan, FaGlobeAmericas } from "react-icons/fa";
import { usePlans, formatPrice, planIsAvailable } from "../hooks/usePlans";
import { useLocale } from "../i18n/LocaleContext";
import SubtleBackground from "./SubtleBackground";
import DateTiles from "./DateTiles";

const TRANSPORT_ICONS: Record<string, React.ReactNode> = {
  vuelo: <FaPlane />,
  crucero: <FaShip />,
  autobus: <FaShip />,
  terrestre: <FaShip />,
  na: <FaPlane />,
}

const PlanDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { data } = usePlans()
  const { locale, t } = useLocale()

  const plan = data.plans.find((p) => p.id === id)
  const destination = plan ? data.destinations.find((d) => d.id === plan.destinationId) : undefined

  if (!plan) {
    return (
      <div className="page plan-detail plan-detail--missing">
        <h1>{t.detail.notFoundTitle}</h1>
        <p>{t.detail.notFoundBody}</p>
        <Link to="/destinos" className="btn-link">← {t.detail.backToCatalog}</Link>
      </div>
    )
  }

  const available = planIsAvailable(plan)
  const availabilityLabel = plan.availability === "consultar" ? t.availability.consultarLong : t.availability[plan.availability]

  return (
    <div className="page plan-detail">
      <SubtleBackground imageIndex={12} />
      <Link to="/destinos" className="btn-link">
        <FaArrowLeft aria-hidden="true" /> {t.detail.allDestinations}
      </Link>

      <div className={`plan-detail-media ${plan.image ? "" : "plan-card-media--placeholder"}`}>
        {plan.image ? (
          <img src={plan.image} alt={plan.title} />
        ) : (
          <span className="plan-card-emoji" aria-hidden="true">🌴</span>
        )}
      </div>

      <p className="plan-destination">
        {(destination && (t.destNames[destination.id] ?? destination.name)) ?? plan.category}
      </p>
      <h1>{plan.title}</h1>
      <p className="meta-line">{t.plan.code(plan.mt)}</p>

      <div className="plan-meta plan-meta--detail">
        {(plan.days != null || plan.nights != null) && (
          <span className="plan-chip">
            <FaMoon aria-hidden="true" /> {plan.days != null ? t.plan.days(plan.days) : ""}
            {plan.days != null && plan.nights != null ? " / " : ""}
            {plan.nights != null ? t.plan.nights(plan.nights) : ""}
          </span>
        )}
        <span className="plan-chip">
          {TRANSPORT_ICONS[plan.transport]} {t.transport[plan.transport] ?? t.transport.na}
        </span>
        {plan.includesFlight && <span className="plan-chip">✈️ {t.plan.includesFlight}</span>}
        {plan.countries.length > 0 && (
          <span className="plan-chip"><FaGlobeAmericas aria-hidden="true" /> {plan.countries.join(", ")}</span>
        )}
        <span className={`plan-availability plan-availability--${plan.availability}`}>
          {availabilityLabel}
        </span>
      </div>

      <p className="plan-price plan-price--detail">
        <strong>
          {plan.price.amount != null
            ? `${formatPrice(plan.price, locale)} ${plan.price.currency}`
            : t.plan.priceOnRequest}
        </strong>
        {plan.price.amount != null && (
          <>
            {plan.price.perPerson && <span> {t.plan.perPerson}</span>}
            {plan.price.roomType && <span> {t.plan.roomType(plan.price.roomType)}</span>}
          </>
        )}
      </p>
      {plan.price.taxes != null && (
        <p className="plan-price-note">{t.plan.taxes(formatPrice({ ...plan.price, amount: plan.price.taxes }, locale), plan.price.currency)}</p>
      )}
      {plan.price.validUntil && (
        <p className="meta-line">{t.plan.validUntil(new Intl.DateTimeFormat(t.localeTag, { dateStyle: "long" }).format(new Date(`${plan.price.validUntil}T00:00:00`)))}</p>
      )}

      <section className="plan-section">
        <h2>{t.detail.dates}</h2>
        <DateTiles dates={plan.dates} />
      </section>

      {plan.cities.length > 0 && (
        <section className="plan-section">
          <h2>{t.detail.cities}</h2>
          <p>{plan.cities.join(" · ")}</p>
        </section>
      )}

      {plan.airlines.length > 0 && (
        <section className="plan-section">
          <h2>{t.detail.airlines}</h2>
          <p>{plan.airlines.join(", ")}</p>
        </section>
      )}

      {plan.includes.length > 0 && (
        <section className="plan-section">
          <h2>{t.detail.includes}</h2>
          <ul className="plan-includes">
            {plan.includes.map((item, i) => (
              <li key={`${plan.id}-inc-${i}`}>
                <FaCheck aria-hidden="true" /> {item}
              </li>
            ))}
          </ul>
        </section>
      )}

      {plan.notIncludes.length > 0 && (
        <section className="plan-section">
          <h2>{t.detail.notIncludes}</h2>
          <ul className="plan-includes plan-includes--not">
            {plan.notIncludes.map((item, i) => (
              <li key={`${plan.id}-noinc-${i}`}>
                <FaBan aria-hidden="true" /> {item}
              </li>
            ))}
          </ul>
        </section>
      )}

      {plan.hotels.length > 0 && (
        <section className="plan-section">
          <h2>{t.detail.hotels}</h2>
          <div className="plan-table-wrap">
            <table className="plan-table">
              <thead>
                <tr><th>{t.detail.hotelCol}</th><th>{t.detail.cityCol}</th><th>{t.detail.typeCol}</th><th>{t.detail.countryCol}</th></tr>
              </thead>
              <tbody>
                {plan.hotels.map((h, i) => (
                  <tr key={`${plan.id}-hotel-${i}`}>
                    <td>{h.name}</td><td>{h.city}</td><td>{h.type}</td><td>{h.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {plan.roomRates.length > 0 && (
        <section className="plan-section">
          <h2>{t.detail.rates}</h2>
          <div className="plan-table-wrap">
            <table className="plan-table">
              <thead>
                <tr><th>{t.detail.occupancyCol}</th><th>{t.detail.yearCol}</th><th>{t.detail.priceCol}</th></tr>
              </thead>
              <tbody>
                {plan.roomRates.map((r, i) => (
                  <tr key={`${plan.id}-rate-${i}`}>
                    <td>{r.occupancy}</td>
                    <td>{r.year}</td>
                    <td>{formatPrice({ amount: r.price, currency: plan.price.currency, perPerson: plan.price.perPerson, taxes: null, roomType: null, validUntil: null }, locale)} {plan.price.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="plan-cta">
        {available ? (
          <Link to="/contact" className="btn-primary btn-as-link">
            {t.detail.quote}
          </Link>
        ) : (
          <p className="banner banner-warn">{t.detail.soldOut}</p>
        )}
      </div>
    </div>
  )
}

export default PlanDetail;
